import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import chartIconSvg from "../../assets/images/charticonsvg.svg";
import tableIconSvg from "../../assets/images/tableiconsvg.svg";
import downloadPngIconSvg from "../../assets/images/downloadpngiconsvg.svg";
import removeIconSvg from "../../assets/images/removeiconsvg.svg";
const KebabIcon = ({ src, alt = "" }) => <img className="pos-dashboard-chart-kebab__icon" src={src} alt={alt} aria-hidden />;

/**
 * Customer Trend ⋯ — Chart (line) / Table only, then Download / Remove (Figma).
 * @param {object} props
 * @param {'chart'|'table'} props.viewKind
 * @param {(k: 'chart'|'table') => void} props.onViewKindChange
 * @param {() => void} [props.onDownload] - PNG download callback
 * @param {() => void} [props.onRemove]
 */
function CustomerTrendKebabMenu({ viewKind, onViewKindChange, onDownload, onRemove }) {
  const { t } = useTranslation();
  const menuId = useId();
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) close();
    };
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close]);

  const pick = (k) => {
    onViewKindChange?.(k);
    close();
  };

  const handleDownload = () => {
    onDownload?.();
    close();
  };

  const handleRemove = () => {
    onRemove?.();
    close();
  };

  return (
    <div className="pos-dashboard-chart-kebab" ref={wrapRef}>
      <button
        type="button"
        className="pos-dashboard-chart-card__kebab"
        aria-label={t("Chart actions", { defaultValue: "Chart actions" })}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden>
          <circle cx="12" cy="6" r="1.75" fill="currentColor" />
          <circle cx="12" cy="12" r="1.75" fill="currentColor" />
          <circle cx="12" cy="18" r="1.75" fill="currentColor" />
        </svg>
      </button>
      {open ? (
        <div id={menuId} className="pos-dashboard-chart-kebab__menu" role="menu">
          <div className="pos-dashboard-chart-kebab__inner">
            <button
              type="button"
              role="menuitem"
              className={`pos-dashboard-chart-kebab__item ${viewKind === "chart" ? "pos-dashboard-chart-kebab__item--hl" : ""}`}
              onClick={() => pick("chart")}
            >
              <span className="pos-dashboard-chart-kebab__item-row">
                <KebabIcon src={chartIconSvg} />
                <span className="pos-dashboard-chart-kebab__label">{t("Chart", { defaultValue: "Chart" })}</span>
              </span>
            </button>
            <button
              type="button"
              role="menuitem"
              className={`pos-dashboard-chart-kebab__item ${viewKind === "table" ? "pos-dashboard-chart-kebab__item--hl" : ""}`}
              onClick={() => pick("table")}
            >
              <span className="pos-dashboard-chart-kebab__item-row">
                <KebabIcon src={tableIconSvg} />
                <span className="pos-dashboard-chart-kebab__label">{t("Table", { defaultValue: "Table" })}</span>
              </span>
            </button>
            <div className="pos-dashboard-chart-kebab__divider" role="separator" />
            <button type="button" role="menuitem" className="pos-dashboard-chart-kebab__item" onClick={handleDownload}>
              <span className="pos-dashboard-chart-kebab__item-row">
                <KebabIcon src={downloadPngIconSvg} />
                <span className="pos-dashboard-chart-kebab__label">{t("Download", { defaultValue: "Download" })}</span>
              </span>
            </button>
            <button type="button" role="menuitem" className="pos-dashboard-chart-kebab__item" onClick={handleRemove}>
              <span className="pos-dashboard-chart-kebab__item-row">
                <KebabIcon src={removeIconSvg} />
                <span className="pos-dashboard-chart-kebab__label pos-dashboard-chart-kebab__label--danger">
                  {t("Remove", { defaultValue: "Remove" })}
                </span>
              </span>
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default CustomerTrendKebabMenu;
